/*
 * Helios Shopify quiz — dependency-free 21-state controller.
 * Preserves the Solar Clinical Editorial cadence, answer-derived feature explanations,
 * mobile-first navigation, optional endpoint submission, and non-sensitive product handoff.
 */
(() => {
  "use strict";

  const QUESTIONS = {
    age: {
      kicker: "First, a little context",
      title: "How old are you?",
      helper: "Choose one, or skip this step.",
      mode: "single",
      options: [
        ["under-30", "Under 30"], ["30-39", "30–39"], ["40-49", "40–49"],
        ["50-59", "50–59"], ["over-60", "Over 60"], ["skip", "Skip this step"]
      ]
    },
    concerns: {
      kicker: "Your starting point",
      title: "What is your biggest oral-care concern right now?",
      helper: "Choose as many as you like.",
      mode: "multi",
      options: [
        ["brightness", "A brighter-looking smile"], ["bleeding", "Bleeding gums"],
        ["breath", "Breath that feels less fresh"], ["plaque", "Plaque or buildup"],
        ["sensitivity", "Sensitive teeth"], ["recession", "Gums pulling back"]
      ]
    },
    areas: {
      kicker: "Where it matters",
      title: "Where should your routine work hardest?",
      helper: "Choose all that apply.",
      mode: "multi",
      options: [
        ["front", "Front teeth"], ["back", "Back teeth"],
        ["gumline", "Gumline and between teeth"], ["tongue", "Tongue and breath"],
        ["unsure", "I’m not sure"]
      ]
    },
    products: {
      kicker: "What you use now",
      title: "Which oral-care products have you tried?",
      helper: "Choose all that apply.",
      mode: "multi",
      options: [
        ["manual", "Manual toothbrush"], ["electric", "Electric toothbrush"],
        ["toothpaste", "Specialist toothpaste"], ["mouthwash", "Mouthwash or rinse"],
        ["whitening", "Whitening products"], ["interdental", "Floss, picks, or Waterpik"],
        ["tongue", "Tongue scraper"]
      ]
    },
    treatments: {
      kicker: "Professional care",
      title: "Have you tried any of these professional treatments?",
      helper: "Choose all that apply.",
      mode: "multi",
      options: [
        ["cleaning", "Professional cleaning"], ["deep-cleaning", "Deep cleaning or root planing"],
        ["gum-treatment", "A gum-focused treatment"], ["cosmetic", "Cosmetic dental work"],
        ["other", "Other"], ["none", "None of the above"]
      ]
    },
    goals: {
      kicker: "What you want next",
      title: "What are your main oral-care goals?",
      helper: "Choose as many as you like.",
      mode: "multi",
      options: [
        ["brightness", "A brighter-looking smile"], ["breath", "Fresher-feeling breath"],
        ["gums", "More confidence in my gum-care routine"], ["plaque", "Less plaque and buildup"],
        ["routine", "A cleaner, more effective routine"], ["all", "All of the above"]
      ]
    },
    time: {
      kicker: "Your existing routine",
      title: "How long do you usually spend brushing?",
      mode: "single",
      options: [
        ["under-2", "Less than 2 minutes"], ["about-2", "About 2 minutes"],
        ["five", "About 5 minutes"], ["over-5", "More than 5 minutes"], ["unsure", "I’m not sure"]
      ]
    },
    light: {
      kicker: "The Helios difference",
      title: "Have you ever used light as part of brushing?",
      mode: "single",
      options: [["yes", "Yes"], ["no", "No"], ["did-not-know", "I didn’t know that was a thing"]]
    },
    replacement: {
      kicker: "Brush-head rhythm",
      title: "How often do you replace your toothbrush or brush head?",
      mode: "single",
      options: [
        ["one-two", "Every 1–2 months"], ["three-four", "Every 3–4 months"],
        ["few-times", "A few times a year"], ["rarely", "Rarely or never"]
      ]
    },
    sensitivity: {
      kicker: "One final detail",
      title: "Do your gums or teeth ever feel sensitive, sore, or irritated?",
      helper: "Product-fit context only—not a dental assessment.",
      mode: "single",
      options: [["yes", "Yes"], ["no", "No"]]
    }
  };

  const SCREENS = [
    { id: "age", type: "question" }, { id: "concerns", type: "question" },
    { id: "severity", type: "question" }, { id: "areas", type: "question" },
    { id: "right-place", type: "interstitial", tone: "blush", kicker: "A different product role", title: "You’re in the right place.", body: "Helios combines a 40,000-strokes-per-minute sonic toothbrush with red, blue, and near-infrared light during the brushing routine you already know.", fact: "3", factLabel: "integrated light wavelengths" },
    { id: "products", type: "question" },
    { id: "products-objection", type: "interstitial", tone: "dark", auto: true, duration: 4200, kicker: "If the usual products felt incomplete", title: "Don’t worry if they still left something unresolved.", body: "Most brushes focus on surface cleaning. Helios keeps that foundation and adds a different light-based product role without asking for another daily treatment step." },
    { id: "treatments", type: "question" },
    { id: "cost-objection", type: "interstitial", tone: "cream", auto: true, duration: 4200, kicker: "Keep the decision in perspective", title: "Professional treatment decisions can feel expensive and heavy.", body: "Helios is an at-home product—not a replacement for recommended care. Its {days}-day decision period lets you test whether the routine fits without treating a creator story as a guaranteed result." },
    { id: "goals", type: "question" }, { id: "time", type: "question" },
    { id: "two-minutes", type: "interstitial", tone: "blush", auto: true, duration: 4200, kicker: "No extra ritual", title: "Your existing two minutes can do more.", body: "Helios adds its light system during brushing, supported by four modes, a two-minute timer, and 40,000 sonic strokes per minute.", fact: "2 MIN", factLabel: "one familiar brushing window" },
    { id: "light", type: "question" },
    { id: "light-education", type: "interstitial", tone: "dark", kicker: "Why the glow feels new", title: "Most people haven’t used light while brushing.", body: "Helios uses 660 nm red and 830 nm near-infrared wavelengths being studied for calming gum inflammation, plus 460 nm blue light known to target odour-causing bacteria. This explains the feature—not a promised clinical outcome." },
    { id: "replacement", type: "question" }, { id: "sensitivity", type: "question" },
    { id: "analyse", type: "loader", duration: 3600, title: "Analysing your oral-care routine…", body: "Concerns · habits · goals" },
    { id: "qualified", type: "qualification", tone: "blue" },
    { id: "calculate", type: "loader", duration: 3600, title: "Calculating your Helios product-fit result…", body: "Wavelengths · sonic cleaning · routine" },
    { id: "result", type: "result" }
  ];

  const QUESTION_IDS = SCREENS.filter((screen) => screen.type === "question").map((screen) => screen.id);
  const CONCERN_LABELS = {
    brightness: "Smile-brightness focus", bleeding: "Bleeding-gum concern", breath: "Freshness focus",
    plaque: "Plaque and buildup", sensitivity: "Sensitivity focus", recession: "Gumline focus"
  };

  const ICONS = {
    arrow: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>',
    external: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M14 5h5v5m0-5-9 9M19 13v6H5V5h6"/></svg>',
    shield: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3 5 6v5c0 4.6 2.9 8.7 7 10 4.1-1.3 7-5.4 7-10V6l-7-3Z"/></svg>',
    check: '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6"/></svg>'
  };


  /* Inline SVG rather than image files: they inherit currentColor so they can
     tint with the selected state, stay crisp on every screen, add no requests,
     and cannot 404 the way a hosted asset can. Keyed by answer value. */
  const OPTION_ICONS = {
    /* concerns */
    brightness: 'M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4m0-12.8L17 7M7 17l-1.4 1.4M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
    bleeding: 'M12 3s6 6.6 6 10.4A6 6 0 0 1 6 13.4C6 9.6 12 3 12 3Z',
    breath: 'M4 9h9a3 3 0 1 0-3-3M4 13h12a3 3 0 1 1-3 3M4 17h7',
    plaque: 'M5 8c0-2.2 1.8-4 4-4 1.2 0 2.2.5 3 1.3A4 4 0 0 1 19 8c0 5-2.4 12-4 12s-1.6-5-3-5-1.4 5-3 5S5 13 5 8Z',
    sensitivity: 'M13 2 5 14h6l-2 8 8-12h-6l2-8Z',
    recession: 'M4 8h16M6 8v4a6 6 0 0 0 12 0V8M9 20h6',
    /* areas */
    front: 'M7 4h10v6a5 5 0 0 1-10 0V4Zm5 11v5',
    back: 'M4 6h6v5a3 3 0 0 1-6 0V6Zm10 0h6v5a3 3 0 0 1-6 0V6ZM7 17h10',
    gumline: 'M4 8h16M6 8v4a6 6 0 0 0 12 0V8M9 20h6',
    tongue: 'M6 5h12v4a6 6 0 0 1-12 0V5Zm2 8c0 3 1.8 6 4 6s4-3 4-6',
    unsure: 'M9.2 9a3 3 0 1 1 4 2.8c-.8.3-1.2 1-1.2 1.8v.4M12 18h.01',
    /* products */
    manual: 'm4 20 9-9M13 5h6v6M11 7l6 6',
    electric: 'M13 2 5 14h6l-2 8 8-12h-6l2-8Z',
    toothpaste: 'M9 3h6v3l2 3v12H7V9l2-3V3Zm1 8h4',
    mouthwash: 'M8 3h8v4l1 3v10H7V10l1-3V3Zm2 9h4',
    whitening: 'M12 4 9 9l-5.5 1 4 4-1 5.5L12 17l5.5 2.5-1-5.5 4-4L15 9Z',
    interdental: 'M5 5c5 3 9 3 14 0M5 19c5-3 9-3 14 0M9 5v14M15 5v14',
    /* treatments */
    cleaning: 'M12 3v6m-4 0h8l-1 11H9L8 9Z',
    'deep-cleaning': 'M12 2v8m-5 0h10l-1.5 12h-7L7 10Zm-2 5h14',
    'gum-treatment': 'M4 8h16M6 8v4a6 6 0 0 0 12 0V8M12 14v6M9 20h6',
    cosmetic: 'M12 4 9 9l-5.5 1 4 4-1 5.5L12 17l5.5 2.5-1-5.5 4-4L15 9Z',
    other: 'M6 12h.01M12 12h.01M18 12h.01',
    none: 'M5 12h14',
    /* goals */
    gums: 'M12 3s6 6.6 6 10.4A6 6 0 0 1 6 13.4C6 9.6 12 3 12 3Z',
    routine: 'M12 6v6l4 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    all: 'm5 12 4 4L19 6M5 19h14',
    /* brushing time */
    'under-2': 'M12 7v5l3 1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    'about-2': 'M12 7v5l3 1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    five: 'M12 7v5l3 1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    'over-5': 'M12 7v5l3 1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    /* light awareness + sensitivity */
    yes: 'm5 12 4 4L19 6',
    no: 'M6 6l12 12M18 6 6 18',
    'did-not-know': 'M9.2 9a3 3 0 1 1 4 2.8c-.8.3-1.2 1-1.2 1.8v.4M12 18h.01',
    /* replacement cadence */
    'one-two': 'M8 4h8v3l-1 13H9L8 7Z',
    'three-four': 'M8 4h8v3l-1 13H9L8 7Zm-3 4h14',
    'few-times': 'M12 6v6l4 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
    rarely: 'M12 8v5M12 17h.01M10.3 3.9 2.4 18a1.8 1.8 0 0 0 1.6 2.7h16a1.8 1.8 0 0 0 1.6-2.7L13.7 3.9a1.8 1.8 0 0 0-3.4 0Z',
    /* age */
    'under-30': 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    '30-39': 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    '40-49': 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    '50-59': 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    'over-60': 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    skip: 'M6 5v14l8-7Zm10 0v14',
    /* severity — a rising three-step scale */
    maintain: 'M4 17h4v3H4zM10 17h4v3h-4zM16 17h4v3h-4z',
    recurring: 'M4 14h4v6H4zM10 11h4v9h-4zM16 17h4v3h-4z',
    'hard-to-ignore': 'M4 14h4v6H4zM10 10h4v10h-4zM16 6h4v14h-4z'
  };

  const optionIcon = (value) => {
    const key = Object.prototype.hasOwnProperty.call(OPTION_ICONS, value) ? value : null;
    if (!key) return "";
    return `<span class="hq-answer__glyph" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="${OPTION_ICONS[key]}"/></svg></span>`;
  };

  /* Reads an answer set back in plain language, so later screens can refer to
     what the person actually said instead of asking in a vacuum. */
  const ANSWER_WORDS = {
    brightness: "a brighter smile", bleeding: "bleeding gums", breath: "freshness",
    plaque: "plaque and buildup", sensitivity: "sensitivity", recession: "your gumline",
    gums: "gum health", teeth: "your teeth", tongue: "your tongue"
  };

  /* STEP 6 — "I'm not sure", "None of the above" and "All of the above" are
     mutually exclusive with every specific answer in their field. Without this
     a person can submit "none" alongside three treatments. */
  const QUIZ_VERSION = "helios-match-1.1";
  const DRAFT_KEY = "helios_match_draft_v1";
  const DRAFT_TTL = 1000 * 60 * 90;   /* 90 minutes */

  const EXCLUSIVE_VALUES = { areas: "unsure", treatments: "none", goals: "all" };

  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;"
  })[character]);

  class HeliosQuiz {
    constructor(root) {
      this.root = root;
      this.screenNode = root.querySelector("[data-hq-screen]");
      this.statusNode = root.querySelector("[data-hq-status]");
      this.progress = root.querySelector(".hq-progress");
      this.progressLabel = root.querySelector("[data-hq-progress-label]");
      this.progressValue = root.querySelector("[data-hq-progress-value]");
      this.progressBar = root.querySelector("[data-hq-progress-bar]");
      this.backButton = root.querySelector('[data-hq-action="back"]');
      this.restartButtons = root.querySelectorAll('[data-hq-action="restart"]:not([data-hq-logo])');
      this.answers = {};
      this.step = 0;
      this.timer = null;
      this.moving = false;
      this.started = false;
      this.config = {
        instanceId: root.dataset.instanceId || "helios-quiz",
        productName: root.dataset.productName || "Helios Red + Blue Light Toothbrush",
        productUrl: root.dataset.productUrl || "/products/helios-red-light-toothbrush",
        productPrice: root.dataset.productPrice || "$149",
        productComparePrice: root.dataset.productComparePrice || "$249",
        productImage: root.dataset.productImage || "",
        socialProof: root.dataset.socialProof || "",
        guaranteeDays: parseInt(root.dataset.guaranteeDays || "60", 10),
        totalCare: {
          url: root.dataset.tcUrl || "", title: root.dataset.tcTitle || "Helios Total-Care System",
          price: root.dataset.tcPrice || "", image: root.dataset.tcImage || "", id: root.dataset.tcId || ""
        },
        starter: {
          url: root.dataset.stUrl || "", title: root.dataset.stTitle || "Helios Starter",
          price: root.dataset.stPrice || "", id: root.dataset.stId || ""
        },
        bundleTotal: root.dataset.bundleTotal || "",
        scienceUrl: root.dataset.scienceUrl || "/pages/science",
        submitEndpoint: root.dataset.submitEndpoint || "",
        autoRedirect: root.dataset.autoRedirect !== "false" && !(window.Shopify && window.Shopify.designMode)
      };
      this.stepTimers = [];
      this.history = [];
      this.restoreDraft();
      this.bind();
      this.render(false);
    }

    bind() {
      this.screenNode.addEventListener("click", (event) => this.handleScreenClick(event));
      this.backButton.addEventListener("click", () => this.back());
      this.restartButtons.forEach((button) => button.addEventListener("click", () => this.restart()));
    }

    handleScreenClick(event) {
      const answer = event.target.closest("[data-hq-answer]");
      if (answer) {
        this.choose(answer.dataset.question, answer.dataset.value, answer.dataset.mode);
        return;
      }
      const action = event.target.closest("[data-hq-action]");
      if (!action) return;
      if (action.dataset.hqAction === "next") this.next();
      if (action.dataset.hqAction === "submit") this.submit(action);
      if (action.dataset.hqAction === "product") this.goToProduct();
      if (action.dataset.hqAction === "starter") {
        this.publish("offer_selected", { offer_id: "starter", product_id: this.config.starter.id, price: this.config.starter.price });
        this.goToProduct("starter");
      }
      if (action.dataset.hqAction === "skip") this.advance();
    }

    choose(questionId, value, mode) {
      if (this.moving) return;
      /* a short tap is most of what makes a native app feel expensive; it is
         a no-op on desktop and on iOS, which does not expose the API */
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (error) {} }
      if (!this.started) {
        this.started = true;
        this.track("helios_quiz_start", { first_field: questionId });
        this.publish("quiz_started", { quiz_version: QUIZ_VERSION, attribution: this.attribution() });
      }

      if (mode === "multi") {
        const current = this.answers[questionId] || [];
        const exclusive = EXCLUSIVE_VALUES[questionId];
        if (exclusive && value === exclusive) {
          /* picking the exclusive option clears everything else */
          this.answers[questionId] = current.includes(value) ? [] : [value];
        } else {
          const rest = exclusive ? current.filter((item) => item !== exclusive) : current;
          this.answers[questionId] = rest.includes(value)
            ? rest.filter((item) => item !== value)
            : [...rest, value];
        }
        this.saveDraft();
        this.render(false);
        window.setTimeout(() => {
          this.screenNode.querySelector(`[data-hq-answer][data-value="${CSS.escape(value)}"]`)?.focus();
        }, 0);
        return;
      }

      this.answers[questionId] = [value];
      this.saveDraft();
      this.moving = true;
      window.setTimeout(() => {
        this.moving = false;
        this.next();
      }, 170);
    }


    /* the forward move, separated so the submit can gate it */
    advance() {
      const screen = SCREENS[this.step];
      if (screen.type === "question") {
        const selected = this.answers[screen.id] || [];
        if (!selected.length) return;
        this.history = this.history || [];
        if (this.history[this.history.length - 1] !== this.step) this.history.push(this.step);
        this.track("helios_quiz_answer", { field: screen.id, selection_count: selected.length });
        this.publish("quiz_answered", { field_id: screen.id, selection_count: selected.length });
      }
      this.step = Math.min(this.step + 1, SCREENS.length - 1);
      this.saveDraft();
      this.render(true);
    }

    next() {
      const screen = SCREENS[this.step];
      this.advance();
    }

    /* STEP 7 — stepping back one index lands on interstitials and loaders,
       which immediately auto-advance forwards again. Walk the history of
       questions the person actually answered instead. */
    back() {
      this.clearTimers();
      const previous = (this.history || []).pop();
      if (previous === undefined) return;
      this.step = previous;
      this.render(true);
      this.saveDraft();
    }

    clearTimers() {
      window.clearTimeout(this.timer);
      this.clearLoaderSteps();
    }


    /* STEP 8 — a refresh mid-quiz should not cost eleven answers. Versioned
       and short-lived, so a future change to the questions cannot restore a
       draft that no longer matches them. */
    saveDraft() {
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
          v: QUIZ_VERSION, savedAt: Date.now(), step: this.step,
          history: this.history || [], answers: this.answers
        }));
      } catch (error) {}
    }

    clearDraft() {
      try { window.sessionStorage.removeItem(DRAFT_KEY); } catch (error) {}
    }

    restoreDraft() {
      try {
        const raw = window.sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return false;
        const draft = JSON.parse(raw);
        if (!draft || draft.v !== QUIZ_VERSION) { this.clearDraft(); return false; }
        if (Date.now() - draft.savedAt > DRAFT_TTL) { this.clearDraft(); return false; }
        if (!draft.answers || !Object.keys(draft.answers).length) return false;
        this.answers = draft.answers;
        this.history = draft.history || [];
        /* never resume onto a loader or a timed screen — step back to the
           question they were last editing */
        let step = draft.step || 0;
        while (step > 0 && SCREENS[step] && SCREENS[step].type !== "question") step -= 1;
        this.step = step;
        this.started = true;
        return true;
      } catch (error) { return false; }
    }

    restart() {
      this.clearTimers();
      this.clearDraft();
      this.answers = {};
      this.history = [];
      this.step = 0;
      this.started = false;
      this.status("");
      this.render(true);
      this.track("helios_quiz_restart");
    }

    render(moveFocus = true) {
      const current = SCREENS[this.step];
      if (current) {
        this.publish("quiz_screen_viewed", {
          screen_id: current.id,
          question_number: this.questionNumber ? this.questionNumber(current) : undefined
        });
      }
      window.clearTimeout(this.timer);
      const screen = SCREENS[this.step];
      this.root.dataset.screen = screen.id;
      this.root.dataset.screenType = screen.type;
      this.root.dataset.tone = screen.tone || "cream";
      this.updateHeader(screen);
      this.updateVisual(screen);

      if (screen.type === "question") this.screenNode.innerHTML = this.questionTemplate(screen.id);
      if (screen.type === "interstitial") this.screenNode.innerHTML = this.interstitialTemplate(screen);
      this.clearLoaderSteps();
      if (screen.type === "loader") {
        this.screenNode.innerHTML = this.loaderTemplate(screen);
        this.runLoaderSteps(screen.duration || 3600);
      }
      if (screen.type === "qualification") this.screenNode.innerHTML = this.qualificationTemplate();
      if (screen.type === "result") this.screenNode.innerHTML = this.resultTemplate();

      this.track("helios_quiz_screen_view", { screen_id: screen.id, screen_number: this.step + 1 });
      if (moveFocus) {
        this.root.scrollIntoView({ behavior: "auto", block: "start" });
        window.setTimeout(() => this.screenNode.focus({ preventScroll: true }), 20);
      }

      if (screen.auto) { this.schedule(() => this.next(), screen.duration || 2400); }
      if (screen.id === "analyse" || screen.id === "calculate") this.schedule(() => this.next(), screen.duration);
      if (screen.id === "offer" && this.config.autoRedirect) this.schedule(() => this.goToProduct(), screen.duration);
    }

    updateHeader(screen) {
      const percent = Math.round((this.step / (SCREENS.length - 1)) * 100);
      const questionNumber = QUESTION_IDS.indexOf(screen.id) + 1;
      const label = screen.type === "question" ? `${questionNumber} of 11` :
        screen.type === "interstitial" ? "Your Helios consultation" :
        screen.type === "loader" ? "Building your result" : "Your Helios match";
      this.progress.setAttribute("aria-valuenow", String(percent));
      this.progressLabel.textContent = label;
      this.progressValue.textContent = `${percent}%`;
      this.progressBar.style.width = `${percent}%`;
      this.backButton.disabled = this.step === 0 || screen.type === "loader" || Boolean(screen.auto);
      this.restartButtons.forEach((button) => { button.hidden = this.step === 0; });
    }

    updateVisual(screen) {
      const reasons = this.getReasons();
      const concerns = this.answers.concerns || [];
      const goals = this.answers.goals || [];
      const topConcern = concerns[0];
      const gum = concerns.some((item) => ["bleeding", "sensitivity", "recession"].includes(item)) || goals.includes("gums");
      const breath = concerns.includes("breath") || goals.includes("breath") || (this.answers.areas || []).includes("tongue");
      const sonic = concerns.some((item) => ["plaque", "brightness"].includes(item)) || (this.answers.products || []).includes("electric");
      const showProduct = ["age", "qualified", "result", "offer"].includes(screen.id);

      this.root.querySelector("[data-hq-product-portrait]").hidden = !showProduct;
      this.root.querySelector("[data-hq-signal-console]").hidden = showProduct;
      this.root.querySelector("[data-hq-context-label]").textContent = topConcern
        ? CONCERN_LABELS[topConcern]
        : (this.answers.age?.length ? "Next, choose your concern" : "Start with your age");
      this.root.querySelector("[data-hq-context-title]").textContent = topConcern ? CONCERN_LABELS[topConcern] : "Building your match";
      this.root.querySelector("[data-hq-lead-reason]").textContent = reasons[0]?.title || "Select answers to build your feature explanation.";

      [["gum", gum], ["nir", gum], ["breath", breath], ["sonic", sonic]].forEach(([name, active]) => {
        this.root.querySelectorAll(`[data-hq-signal="${name}"], [data-hq-rail="${name}"]`).forEach((node) => {
          node.classList.toggle("is-active", Boolean(active));
        });
      });
    }


    /* Says their earlier answer back to them before asking the next one. The
       quiz already knows the concern; without this every screen reads the same
       for everybody, which is what makes a funnel feel like a form. */

    /* Optional and merchant-controlled: an unsubstantiated number is worse
       than none, so this renders only when the setting is filled in. */

    /* STEP 3 — one source of truth for the money-back period. Screen copy
       carries a {days} token so the number can never disagree between the
       quiz, the result and the product page. */
    days(text) {
      return String(text || "").replace(/\{days\}/g, this.config.guaranteeDays);
    }

    socialProof() {
      const text = this.config.socialProof;
      if (!text) return "";
      const star = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m12 3 2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 16.8 6.6 19.6l1.2-6L3.3 9.4l6.1-.8Z"/></svg>';
      return `<p class="hq-socialproof">${star}${escapeHtml(text)}</p>`;
    }

    echoFor(id) {
      const concerns = this.answers.concerns || [];
      const lead = concerns[0];
      const word = lead ? ANSWER_WORDS[lead] || CONCERN_LABELS[lead] : null;
      const lines = {
        areas: word ? `Because you told us about ${word}, this next one matters.` : null,
        products: word ? `You mentioned ${word}. Let's see what you have already tried.` : null,
        treatments: "Nothing here changes the recommendation — it sets the context.",
        goals: word ? `Keeping ${word} in mind.` : null,
        time: "Almost there.",
        light: null,
        replacement: null,
        sensitivity: "Last question."
      };
      const milestones = { products: "Halfway. Your profile is taking shape." };
      const text = milestones[id] || lines[id];
      if (!text) return "";
      const kind = milestones[id] ? " hq-echo--milestone" : "";
      return `<p class="hq-echo${kind}">${escapeHtml(text)}</p>`;
    }

    /* A row of chips showing what they actually said, so the recommendation
       reads as earned rather than predetermined. */
    recapChips() {
      /* Dedupe on the answer VALUE, not the label: concerns and goals share
         values like "brightness" but render as "Smile-brightness focus" and
         "a brighter smile", which read as two answers when they are one. */
      const seen = new Set();
      const chips = [];
      const collect = (field, dict) => {
        (this.answers[field] || []).forEach((value) => {
          if (seen.has(value)) return;
          const label = dict[value] || CONCERN_LABELS[value] || ANSWER_WORDS[value];
          if (!label) return;
          seen.add(value);
          chips.push(label);
        });
      };
      collect("concerns", CONCERN_LABELS);
      collect("goals", ANSWER_WORDS);
      collect("areas", ANSWER_WORDS);
      if (!chips.length) return "";
      const markup = chips.slice(0, 5)
        .map((label) => `<span>${escapeHtml(label)}</span>`).join("");
      return `<div class="hq-recap"><small>Your answers</small><div>${markup}</div></div>`;
    }

    questionTemplate(id) {
      const question = id === "severity" ? this.severityQuestion() : QUESTIONS[id];
      const selected = this.answers[id] || [];
      const questionNumber = QUESTION_IDS.indexOf(id) + 1;
      const echo = this.echoFor(id);
      const intro = id === "age" ? `
        <div class="hq-entry">
          <span>Two-minute Helios consultation</span>
          <h1>Find the light-supported brush that fits your routine.</h1>
          <p>Eleven quick questions build your feature explanation and current Helios product match.</p>
          ${this.socialProof()}
        </div>` : "";
      const options = question.options.map(([value, label], index) => {
        const active = selected.includes(value);
        return `<button class="hq-answer${active ? " is-selected" : ""}" type="button"
          data-hq-answer data-question="${escapeHtml(id)}" data-value="${escapeHtml(value)}" data-mode="${question.mode}"
          aria-pressed="${active}">
          <span class="hq-answer__index">${String(index + 1).padStart(2, "0")}</span>
        ${optionIcon(value)}
          <span class="hq-answer__copy"><strong>${escapeHtml(label)}</strong></span>
          <span class="hq-answer__icon">${active ? ICONS.check : ICONS.arrow}</span>
        </button>`;
      }).join("");
      const action = question.mode === "multi" ? `
        <div class="hq-mobile-action${selected.length ? " is-ready" : ""}">
          <button class="hq-primary" type="button" data-hq-action="next" ${selected.length ? "" : "disabled"}>
            Continue with ${selected.length} selected ${ICONS.arrow}
          </button>
        </div>` : "";

      return `<div class="hq-question${question.mode === "multi" ? " hq-question--multi" : ""}">
        ${intro}
        <div class="hq-question__meta"><span>${questionNumber} of 11</span><span>${question.mode === "multi" ? "Choose all that fit" : "Choose one"}</span></div>
        ${echo}<span class="hq-eyebrow">${escapeHtml(question.kicker)}</span>
        <h2>${escapeHtml(question.title)}</h2>
        ${question.helper ? `<p class="hq-helper">${escapeHtml(question.helper)}</p>` : ""}
        <div class="hq-folio"><span>Helios fit folio</span><i></i><p>Use Back to revise any answer</p></div>
        <div class="hq-answers${question.options.length > 4 ? " hq-answers--compact" : ""}">${options}</div>
        ${action}
      </div>`;
    }

    severityQuestion() {
      const concerns = this.answers.concerns || [];
      const selected = ["brightness", "bleeding", "breath", "plaque", "sensitivity", "recession"].find((item) => concerns.includes(item)) || "plaque";
      const variants = {
        brightness: ["How noticeable is the change in your smile?", [["maintain", "Hardly noticeable—I want to maintain"], ["recurring", "Noticeable in certain areas"], ["hard-to-ignore", "Clearly visible or uneven overall"]]],
        bleeding: ["How often do you notice bleeding?", [["maintain", "Rarely"], ["recurring", "Sometimes"], ["hard-to-ignore", "Frequently enough to concern me"]]],
        breath: ["How noticeable is the freshness concern?", [["maintain", "Occasional—I want to maintain"], ["recurring", "It returns during the day"], ["hard-to-ignore", "It is difficult to ignore"]]],
        plaque: ["How quickly does the clean feeling fade?", [["maintain", "I mainly want to maintain"], ["recurring", "Buildup returns between brushes"], ["hard-to-ignore", "My mouth rarely feels fully clean"]]],
        sensitivity: ["How often do you notice sensitivity?", [["maintain", "Rarely"], ["recurring", "Sometimes"], ["hard-to-ignore", "Frequently enough to change my routine"]]],
        recession: ["How noticeable are changes around your gumline?", [["maintain", "Hardly noticeable—I want to maintain"], ["recurring", "Noticeable in certain areas"], ["hard-to-ignore", "Clearly visible or hard to ignore"]]]
      };
      return { kicker: "A closer look", title: variants[selected][0], helper: "Selecting one answer moves to the next step.", mode: "single", options: variants[selected][1] };
    }

    interstitialTemplate(screen) {
      return `<div class="hq-interstitial${screen.auto ? " hq-interstitial--timed" : ""}">
        <div class="hq-interstitial__copy">
          <span class="hq-eyebrow">${escapeHtml(screen.kicker)}</span>
          <h2>${escapeHtml(this.days(screen.title))}</h2>
          <p>${escapeHtml(this.days(screen.body))}</p>
          ${screen.fact ? `<div class="hq-fact"><strong>${escapeHtml(screen.fact)}</strong><span>${escapeHtml(screen.factLabel)}</span></div>` : ""}
          ${screen.auto ? `<button class="hq-skip" type="button" data-hq-action="skip">Skip</button>
        <div class="hq-timed" style="--hq-duration:${screen.duration}ms"><span></span><small>Continuing automatically</small></div>` : `
            <div class="hq-interstitial__actions">
              <button class="hq-primary" type="button" data-hq-action="next">Continue ${ICONS.arrow}</button>
              ${screen.id === "light-education" ? `<a href="${escapeHtml(this.config.scienceUrl)}" target="_blank" rel="noopener">See the research ${ICONS.external}</a>` : ""}
            </div>`}
        </div>
      </div>`;
    }


    /* The steps name what the person actually said, and are driven on a timer
       rather than being decorative: pending, then working, then done. A static
       greyed list reads as a placeholder; this reads as the quiz doing the
       work it says it is doing. */
    loaderSteps() {
      const concerns = this.answers.concerns || [];
      const lead = concerns[0];
      const word = lead ? (ANSWER_WORDS[lead] || String(CONCERN_LABELS[lead]).toLowerCase()) : "your routine";
      const answered = Object.keys(this.answers).filter((key) => (this.answers[key] || []).length).length;
      const gum = concerns.some((item) => ["bleeding", "sensitivity", "recession"].includes(item))
        || (this.answers.goals || []).includes("gums");
      const breath = concerns.includes("breath") || (this.answers.goals || []).includes("breath");
      const bands = gum && breath ? "660, 460 and 830 nm" : breath ? "460 nm" : gum ? "660 and 830 nm" : "three wavelengths";
      const minutes = (this.answers.time || [])[0];
      const timeLine = minutes === "under-2" ? "Checking your brushing time against the 2-minute cycle"
        : minutes === "about-2" ? "Confirming your 2 minutes can carry the light"
        : "Checking how the light fits your brushing time";
      return [
        `Reviewing your ${answered} answers`,
        `Matching ${bands} to ${word}`,
        timeLine,
        "Building your feature profile"
      ].map((text) => `<li><span class="hq-step__mark" aria-hidden="true"></span><span>${escapeHtml(text)}</span></li>`).join("");
    }

    /* Advances the steps across the screen's own duration, so the last one
       completes just before the quiz moves on. */
    runLoaderSteps(duration) {
      /* Deliberately NOT this.schedule(): that keeps a single timer id and
         each call overwrites the last, so only the first and final ticks ever
         fired — which is why the list jumped from one step to all four. These
         are tracked separately and cleared on the next render. */
      this.clearLoaderSteps();
      const list = this.screenNode.querySelector("[data-hq-steps]");
      if (!list) return;
      const items = Array.prototype.slice.call(list.children);
      if (!items.length) return;

      const setState = (index) => {
        items.forEach((item, position) => {
          item.classList.toggle("is-active", position === index);
          item.classList.toggle("is-done", position < index);
        });
      };

      /* every step gets an equal share, and the last one holds its finished
         state long enough to be read before the screen changes */
      const hold = 420;
      const slice = Math.max(520, (duration - hold) / items.length);

      setState(0);
      items.forEach((item, index) => {
        if (index === 0) return;
        this.stepTimers.push(window.setTimeout(() => setState(index), Math.round(slice * index)));
      });
      this.stepTimers.push(window.setTimeout(() => {
        items.forEach((item) => {
          item.classList.remove("is-active");
          item.classList.add("is-done");
        });
      }, Math.round(slice * items.length)));
    }

    clearLoaderSteps() {
      (this.stepTimers || []).forEach((id) => window.clearTimeout(id));
      this.stepTimers = [];
    }

    loaderTemplate(screen) {
      const productButton = screen.id === "offer" && !this.config.autoRedirect
        ? `<button class="hq-primary hq-loader__button" type="button" data-hq-action="product">Continue to Helios ${ICONS.arrow}</button>` : "";
      return `<div class="hq-loader">
        <div class="hq-loader__solar"><span class="hq-sun" aria-hidden="true"><i></i><i></i><i></i></span><i></i><i></i><i></i></div>
        <span class="hq-eyebrow">Helios consultation</span>
        <h2>${escapeHtml(this.days(screen.title))}</h2>
        <p>${escapeHtml(this.days(screen.body))}</p>
        <ol class="hq-loader__steps" data-hq-steps>${this.loaderSteps()}</ol>
        <div class="hq-loader__line" style="--hq-duration:${screen.duration}ms"><span></span></div>
        ${productButton}
      </div>`;
    }

    qualificationTemplate() {
      const lead = this.getReasons()[0];
      return `<div class="hq-qualification">
        <span class="hq-eyebrow">Great news—your routine is a fit</span>
        <h2>Your Helios feature profile is ready.</h2>
        <p>Your answers make <strong>${escapeHtml(lead.title.toLowerCase())}</strong> the lead product-fit reason. This is a feature match, not a diagnosis or promised outcome.</p>
        ${this.recapChips()}
        <div class="hq-qualification__signal"><span>${escapeHtml(lead.code)}</span><div><small>Lead feature</small><strong>${escapeHtml(lead.title)}</strong></div></div>
        <button class="hq-primary" type="button" data-hq-action="submit">Calculate my result ${ICONS.arrow}</button>
        <small class="hq-disclaimer">Helios does not replace professional advice, check-ups, or recommended treatment.</small>
      </div>`;
    }

    resultTemplate() {
      const reasons = this.getReasons().map((reason) => `<article class="hq-reason hq-reason--${reason.color}">
        <span>${escapeHtml(reason.code)}</span><div><h3>${escapeHtml(reason.title)}</h3><p>${escapeHtml(reason.body)}</p></div>
      </article>`).join("");

      const tc = this.config.totalCare;
      const st = this.config.starter;
      const days = this.config.guaranteeDays;

      /* STEP 2 — Total-Care is the primary result, Starter the smaller
         alternative. Every value comes from the selected Shopify product;
         nothing about price or URL is written into this file. */
      const saving = this.savingLine();
      const image = tc.image
        ? `<img src="${escapeHtml(tc.image)}" alt="${escapeHtml(tc.title)}" width="320" height="420">`
        : "";
      const secondary = st.url
        ? `<div class="hq-result__alt">
             <p>Prefer the essentials?</p>
             <button class="hq-secondary" type="button" data-hq-action="starter">
               Choose ${escapeHtml(st.title)}${st.price ? " — " + escapeHtml(st.price) : ""}
             </button>
           </div>`
        : "";

      /* DOM order matches reading order: headline, offer, CTA, guarantee,
         then the reasoning. */
      return `<div class="hq-result">
        <div class="hq-result__intro"><span class="hq-eyebrow">Based on your answers</span><h2>Your recommended system</h2><p>Your answers explain which Helios features are most relevant to your routine. The system stays the same; the reasons change.</p></div>
        <div class="hq-result__offer">
          <div class="hq-result__product">${image}</div>
          <div><span class="hq-eyebrow">Your recommended system</span><h3>${escapeHtml(tc.title)}</h3><p>Sonic cleaning · three light wavelengths · complete routine</p></div>
          <div class="hq-result__price"><span>Your quiz offer</span><strong>${escapeHtml(tc.price)}</strong></div>
          ${saving}
        </div>
        <div class="hq-result__actions">
          <button class="hq-primary" type="button" data-hq-action="product">Get the ${escapeHtml(tc.title)}${tc.price ? " — " + escapeHtml(tc.price) : ""} ${ICONS.arrow}</button>
          ${secondary}
          <a href="${escapeHtml(this.config.scienceUrl)}" target="_blank" rel="noopener">Review the science ${ICONS.external}</a>
        </div>
        <div class="hq-result__guarantee">${ICONS.shield}<div><strong>${days} days to decide.</strong><p>Creator stories are personal experiences. Results are not guaranteed.</p></div></div>
        ${this.socialProof()}
        <div class="hq-result__reasons">${reasons}</div>
        ${this.recapChips()}
      </div>`;
    }

    /* Only shown when the merchant has entered a bought-separately total, so
       the saving can never be a number this file invented. */
    savingLine() {
      const separate = this.config.bundleTotal;
      if (!separate) return "";
      return `<div class="hq-result__saving">
        <span>Bought separately</span><del>${escapeHtml(separate)}</del>
      </div>`;
    }

    getReasons() {
      const concerns = this.answers.concerns || [];
      const goals = this.answers.goals || [];
      const reasons = [];
      if (concerns.some((item) => ["bleeding", "sensitivity", "recession"].includes(item)) || goals.includes("gums")) {
        reasons.push({ code: "660 + 830", title: "Gum-care relevance", body: "Your answers make the red and near-infrared features relevant. These wavelengths are being studied for calming gum inflammation.", color: "red" });
      }
      if (concerns.includes("breath") || goals.includes("breath") || (this.answers.areas || []).includes("tongue")) {
        reasons.push({ code: "460", title: "Freshness relevance", body: "Your freshness context brings blue light into the fit. Blue light is known to target odour-causing bacteria.", color: "blue" });
      }
      if (concerns.some((item) => ["plaque", "brightness"].includes(item)) || goals.some((item) => ["plaque", "brightness", "routine", "all"].includes(item)) || (this.answers.products || []).includes("electric")) {
        reasons.push({ code: "40K", title: "Sonic-clean foundation", body: "The light system sits on a 40,000-strokes-per-minute sonic base for the surface-cleaning part of your routine.", color: "black" });
      }
      if ((this.answers.time || []).includes("about-2") || reasons.length < 2) {
        reasons.push({ code: "2 MIN", title: "Built into brushing", body: "Helios adds its light system during the brushing routine you already have instead of creating another daily treatment step.", color: "infrared" });
      }
      return reasons.slice(0, 3);
    }

    /* STEP 1 — one POST per completion, with the fix-list payload shape.
       A configured endpoint that fails must NOT drop the answers or move on
       silently: the button re-enables, an alert is shown, and the selections
       stay exactly where they were. */
    async submit(button) {
      const endpoint = this.config.submitEndpoint;
      this.responseId = this.responseId || this.makeId();

      const payload = {
        quiz_version: QUIZ_VERSION,
        response_id: this.responseId,
        submitted_at: new Date().toISOString(),
        answers: this.answers,
        result_id: "total-care",
        attribution: this.attribution()
      };

      if (!endpoint) {
        /* nothing configured yet — record the fact rather than implying a save */
        this.track("helios_quiz_submit", { storage: "none", quiz_result: "total-care" });
        this.publish("quiz_submitted", { response_id: this.responseId, result_id: "total-care", storage: "none" });
        this.advance();
        return;
      }

      const existing = this.screenNode.querySelector("[data-hq-error]");
      if (existing) existing.remove();
      button.disabled = true;
      button.dataset.originalLabel = button.dataset.originalLabel || button.innerHTML;
      button.textContent = "Saving your answers…";
      this.status("Saving your answers.");

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          keepalive: true,
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.submitState = "sent";
        this.status("Answers saved.");
        this.track("helios_quiz_submit", { storage: "endpoint", quiz_result: "total-care" });
        this.publish("quiz_submitted", { response_id: this.responseId, result_id: "total-care" });
        this.clearDraft();
        this.advance();
      } catch (error) {
        button.disabled = false;
        button.innerHTML = button.dataset.originalLabel;
        this.status("We could not save your answers. Please try again.");
        this.track("helios_quiz_submit_error", { message: error.message });
        const alertNode = document.createElement("p");
        alertNode.className = "hq-error";
        alertNode.setAttribute("data-hq-error", "");
        alertNode.setAttribute("role", "alert");
        alertNode.textContent = "We could not save your answers. Your selections are safe — tap the button to try again.";
        (this.screenNode.querySelector(".hq-qualification") || this.screenNode).appendChild(alertNode);
      }
    }

    attribution() {
      const params = new URLSearchParams(window.location.search);
      const allowed = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ad_id", "creative_id"];
      return allowed.reduce((values, name) => {
        if (params.has(name)) values[name] = params.get(name);
        return values;
      }, {});
    }

    /* STEP 2 — Total-Care by default, Starter when the secondary CTA is used.
       Attribution rides along; oral-health answers never do. */
    goToProduct(offer) {
      const which = offer || this.pendingOffer || "total-care";
      const target = which === "starter" ? this.config.starter : this.config.totalCare;
      const url = target && target.url ? target.url : this.config.productUrl;
      const destination = new URL(url, window.location.origin);
      destination.searchParams.set("quiz_result", which);
      const attribution = this.attribution();
      Object.entries(attribution).forEach(([key, value]) => {
        if (value) destination.searchParams.set(key, value);
      });
      this.publish("product_handoff", { offer_id: which, destination: destination.pathname });
      this.track("helios_quiz_product_handoff", { quiz_result: which });
      this.clearDraft();
      const href = destination.toString();
      /* a seam so the handoff can be asserted in tests without navigating */
      if (typeof window.__nav === "function") { window.__nav(href); return; }
      window.location.assign(href);
    }

    /* STEP 5 — publish through Shopify's own analytics bus so Customer Events
       and Pixel Helper see the sequence. dataLayer is kept for GA4 where it is
       present. Health-adjacent answer VALUES are never sent — only counts. */

    makeId() {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      return "hq-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
    }

    attribution() {
      const params = new URLSearchParams(window.location.search);
      const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ad_id", "creative_id"];
      const out = {};
      keys.forEach((key) => { out[key] = params.get(key) || ""; });
      return out;
    }

    publish(name, data) {
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish("helios:" + name, data || {});
        }
      } catch (error) {}
    }

    track(eventName, parameters = {}) {
      const payload = { event: eventName, quiz_id: this.config.instanceId, ...parameters };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === "function") window.gtag("event", eventName, parameters);
      this.root.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: payload }));
    }

    schedule(callback, delay) {
      this.timer = window.setTimeout(callback, delay || 1100);
    }

    status(message) {
      this.statusNode.textContent = message;
    }
  }

  function mount(scope = document) {
    scope.querySelectorAll("[data-helios-quiz]").forEach((root) => {
      if (root.dataset.hqMounted === "true") return;
      root.dataset.hqMounted = "true";
      root.heliosQuiz = new HeliosQuiz(root);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => mount());
  else mount();

  document.addEventListener("shopify:section:load", (event) => mount(event.target));
  document.addEventListener("shopify:section:select", (event) => {
    const root = event.target.querySelector?.("[data-helios-quiz]");
    if (root?.heliosQuiz) root.heliosQuiz.restart();
  });
})();
